import React from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import PhaseHeader from "@/components/PhaseHeader";
import PhaseFooter from "@/components/PhaseFooter";
import LabeledField from "@/components/LabeledField";
import HourlyRatePill from "@/components/HourlyRatePill";
import { Wrench, Package } from "lucide-react";
import ToggleField from "@/components/ToggleField";
import { primoBreakdown, FRAME_A_DS_LEN_M, FRAME_A_CP_LEN_M, FRAME_B_DS_LEN_M } from "@/lib/simulation-defaults";

function SubtotalRow({ label, value, accent = false, testId }) {
  return (
    <div
      className="mt-2 pt-2 border-t border-[color:var(--border)] flex items-center justify-between gap-3"
      data-testid={testId}
    >
      <span
        className={`text-[10px] font-mono uppercase tracking-[0.16em] ${
          accent ? "text-[color:var(--brand)]" : "text-[color:var(--text-mute)]"
        }`}
      >
        {label}
      </span>
      <span
        className={`font-mono font-semibold tabular-num ${
          accent ? "text-[color:var(--brand)] text-base font-bold" : "text-[color:var(--text)] text-sm"
        }`}
      >
        € {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

function ResultChip({ label, value, testId }) {
  return (
    <div className="bg-[color:var(--surface-2)] border border-[color:var(--border-strong)] rounded px-3 py-2 flex items-center justify-between" data-testid={testId}>
      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[color:var(--text-mute)]">{label}</span>
      <span className="text-sm font-bold font-mono tabular-num text-[color:var(--text)]">
        € {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

export default function Phase3PrimoCosts() {
  const { state, updatePrimo, updateTrailer } = useSimulation();
  const { t } = useI18n();
  const p = state.primo;
  const sidesDiffer = !!state.trailer.sides_differ;
  const doors = Number(state.trailer.doors_per_trailer) || 2;
  const hourlyRate = Number(state.trailer.labor_hourly_rate) || 12;
  const thickness = Number(state.trailer.profile_thickness_mm) || 85;

  const pb = primoBreakdown(p, sidesDiffer, doors, hourlyRate, state.trailer);
  const runningTotal = pb.total;

  const fmtEur = (v) => `€ ${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="wizard-phase phase-in max-w-7xl mx-auto px-4 sm:px-6">
      <PhaseHeader
        eyebrow={t("phases.p3")}
        title={t("primo.title")}
        subtitle={t("primo.subtitle")}
      />

      <div className="wizard-body">
        <div className="wizard-body-scroll grid grid-cols-1 lg:grid-cols-2 gap-3 auto-rows-min">
          {/* Profiles */}
          <section className="card-tech p-3 lg:p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">
                  {t("primo.profiles")}
                </h3>
              </div>
              <ToggleField
                id="primo-sides-toggle"
                label={t("customer.sidesDiffer")}
                checked={sidesDiffer}
                onCheckedChange={(v) => updateTrailer({ sides_differ: v })}
                testId="primo-sides-toggle"
              />
            </div>

            {!sidesDiffer && (
              <div className="space-y-2">
                <LabeledField
                  id="p-ds-price"
                  label={`${t("primo.doorsealing")} ${thickness}mm`}
                  value={p.doorsealing_price_per_m}
                  onChange={(v) => updatePrimo({ doorsealing_price_per_m: v, right_doorsealing_price_per_m: v })}
                  unit="€/m"
                  testId="primo-doorsealing-price"
                />
                <LabeledField
                  id="p-glue"
                  label={t("primo.glue")}
                  value={p.glue_cost}
                  onChange={(v) => updatePrimo({ glue_cost: v })}
                  unit="€"
                  testId="primo-glue-cost"
                />
              </div>
            )}

            {sidesDiffer && (
              <div className="space-y-2" data-testid="primo-sides-split">
                <div className="grid grid-cols-2 gap-2">
                  <div className="border border-[color:var(--brand)]/30 bg-[color:var(--brand-soft)] rounded p-2 space-y-2">
                    <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[color:var(--brand)] leading-tight font-bold">
                      ◄ Porta Esq. · {FRAME_A_DS_LEN_M}m + {FRAME_A_CP_LEN_M}m CP
                    </div>
                    <LabeledField
                      id="p-ds-l"
                      label={`${t("primo.doorsealing")} ${thickness}mm`}
                      value={p.doorsealing_price_per_m}
                      onChange={(v) => updatePrimo({ doorsealing_price_per_m: v })}
                      unit="€/m"
                      testId="primo-doorsealing-price"
                    />
                    <LabeledField
                      id="p-cp-l"
                      label={`${t("primo.centerprofile")} ${thickness}mm`}
                      value={p.centerprofile_price_per_m}
                      onChange={(v) => updatePrimo({ centerprofile_price_per_m: v })}
                      unit="€/m"
                      testId="primo-centerprofile-price"
                    />
                  </div>
                  <div className="border border-[color:var(--border)] rounded p-2 space-y-2">
                    <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[color:var(--text-mute)] leading-tight font-bold">
                      Porta Dir. · {FRAME_B_DS_LEN_M}m ►
                    </div>
                    <LabeledField
                      id="p-ds-r"
                      label={`${t("primo.doorsealing")} ${thickness}mm`}
                      value={p.right_doorsealing_price_per_m}
                      onChange={(v) => updatePrimo({ right_doorsealing_price_per_m: v })}
                      unit="€/m"
                      testId="primo-doorsealing-price-right"
                    />
                    <LabeledField
                      id="p-cp-r"
                      label={`${t("primo.centerprofile")} ${thickness}mm`}
                      value={p.right_centerprofile_price_per_m}
                      onChange={(v) => updatePrimo({ right_centerprofile_price_per_m: v })}
                      unit="€/m"
                      testId="primo-centerprofile-price-right"
                    />
                  </div>
                </div>
                <LabeledField
                  id="p-glue"
                  label={t("primo.glue")}
                  value={p.glue_cost}
                  onChange={(v) => updatePrimo({ glue_cost: v })}
                  unit="€"
                  testId="primo-glue-cost"
                />
                <div className="grid grid-cols-2 gap-2">
                  <ResultChip label="Porta Esq." value={pb.frameAProfiles} testId="primo-frame-a-cost" />
                  <ResultChip label="Porta Dir." value={pb.frameBProfiles} testId="primo-frame-b-cost" />
                </div>
              </div>
            )}

            <SubtotalRow
              label={`${t("common.subtotal")} · ${t("primo.profiles")}`}
              value={pb.profiles}
              testId="primo-subtotal-profiles"
            />
            <SubtotalRow
              label={`${t("common.subtotal")} · ${t("primo.glue")} (× ${doors})`}
              value={pb.materials}
              testId="primo-subtotal-materials"
            />
          </section>

          {/* Labor */}
          <section className="card-tech p-3 lg:p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">{t("primo.labor")}</h3>
              </div>
              <HourlyRatePill testId="primo-hourly-rate-pill" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <LabeledField
                  id="p-wc-t"
                  label={`${t("primo.weldingCutting")} (4 cantos)`}
                  value={p.welding_cutting_time_min}
                  onChange={(v) => updatePrimo({ welding_cutting_time_min: v })}
                  unit="min"
                  testId="primo-welding-time"
                />
                <div className="mt-1.5 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-2 py-1 text-center">
                  <span className="text-xs font-bold font-mono tabular-num text-[color:var(--text)]" data-testid="primo-welding-cost-hint">
                    {fmtEur(pb.weldingCost)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-mute)] ml-1">/ porta</span>
                </div>
              </div>
              <div>
                <LabeledField
                  id="p-gl-t"
                  label={t("primo.gluing")}
                  value={p.gluing_time_min}
                  onChange={(v) => updatePrimo({ gluing_time_min: v })}
                  unit="min"
                  testId="primo-gluing-time"
                />
                <div className="mt-1.5 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-2 py-1 text-center">
                  <span className="text-xs font-bold font-mono tabular-num text-[color:var(--text)]" data-testid="primo-gluing-cost-hint">
                    {fmtEur(pb.gluingCost)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-mute)] ml-1">/ porta</span>
                </div>
              </div>
              <div>
                <LabeledField
                  id="p-mt-t"
                  label={t("primo.mounting")}
                  value={p.mounting_time_min}
                  onChange={(v) => updatePrimo({ mounting_time_min: v })}
                  unit="min"
                  testId="primo-mounting-time"
                />
                <div className="mt-1.5 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-2 py-1 text-center">
                  <span className="text-xs font-bold font-mono tabular-num text-[color:var(--text)]" data-testid="primo-mounting-cost-hint">
                    {fmtEur(pb.mountingCost)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-mute)] ml-1">/ porta</span>
                </div>
              </div>
            </div>

            <SubtotalRow
              label={`${t("common.subtotal")} · ${t("primo.labor")} (× ${doors})`}
              value={pb.labor}
              testId="primo-subtotal-labor"
            />
          </section>

          {/* Totals */}
          <section className="card-tech p-3 lg:p-4 lg:col-span-2 border-[color:var(--brand)]/50" data-testid="primo-totals-card">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-3.5 h-3.5 text-[color:var(--brand)]" />
              <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">
                {t("primo.totalPrice")}
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <ResultChip label="Porta Esq." value={pb.frameATotal} testId="primo-frame-a-total" />
              <ResultChip label="Porta Dir." value={pb.frameBTotal} testId="primo-frame-b-total" />
            </div>

            <SubtotalRow
              label={`${t("primo.totalPrice")}`}
              value={runningTotal}
              accent
              testId="primo-running-total"
            />
          </section>
        </div>
      </div>

      <PhaseFooter />
    </div>
  );
}
